import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  Stack,
  useTheme as useMuiTheme,
  alpha,
} from '@mui/material';
import { useTheme as useThemeContext } from '../../../theme/ThemeProvider';
import { ThemeMode } from '../../../constants/interfaces/themeSettings';
import { useTranslation } from '../../../services/translation';

export interface ThemeVisualSelectorProps {
  currentMode: ThemeMode;
  onModeChange: (mode: ThemeMode) => void;
  disabled?: boolean;
}

const ThemePreviewCard: React.FC<{
  mode: 'light' | 'dark';
  isSelected: boolean;
  onSelect: () => void;
  disabled?: boolean;
}> = ({ mode, isSelected, onSelect, disabled = false }) => {
  const muiTheme = useMuiTheme();
  
  const isDark = mode === 'dark';
  // Match provided visual: soft outer surface with large rounded corners
  const bgColor = isDark ? '#343434' : '#f3f3f3';
  const contentBg = isDark ? '#2a2a2a' : '#ffffff';
  const textColor = isDark ? '#ffffff' : '#000000';
  // Accent color not used currently
  
  return (
    <Card
      onClick={onSelect}
      sx={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        border: `2px solid ${isSelected ? muiTheme.palette.primary.main : 'transparent'}`,
        borderRadius: 4,
        transition: 'all 0.2s ease-in-out',
        '&:hover': disabled ? {} : {
          transform: 'translateY(-2px)',
          boxShadow: muiTheme.shadows[4],
        },
        backgroundColor: bgColor,
        aspectRatio: '4 / 5', // Taller tile like the reference
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        boxShadow: `inset 0 0 0 1px ${alpha(textColor, 0.06)}`,
      }}
    >
      <CardContent sx={{ 
        p: { xs: 1.5, sm: 2, md: 2.5 }, 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        {/* Simulated app window */}
        <Box sx={{ 
          mb: { xs: 2, sm: 2.5, md: 3 }, 
          width: '100%',
          maxWidth: '300px',
          mx: 'auto',
        }}>
          {/* Top header with app bars */}
          <Box
            sx={{
              height: { xs: 20, sm: 22, md: 24 },
              backgroundColor: contentBg,
              borderRadius: '12px 12px 0 0',
              display: 'flex',
              alignItems: 'center',
              px: { xs: 0.75, sm: 1, md: 1.25 },
              gap: { xs: 0.75, sm: 1, md: 1.25 },
            }}
          >
            {/* Left app bar */}
            <Box sx={{ width: { xs: 80, sm: 100, md: 120 }, height: { xs: 10, sm: 11, md: 12 }, backgroundColor: alpha(textColor, 0.1), borderRadius: { xs: 0.75, sm: 1, md: 1.25 } }} />
            {/* Center pill */}
            <Box sx={{ width: { xs: 18, sm: 20, md: 22 }, height: { xs: 10, sm: 11, md: 12 }, backgroundColor: alpha(textColor, 0.08), borderRadius: 10 }} />
            {/* Right app bar */}
            <Box sx={{ flex: 1, height: { xs: 10, sm: 11, md: 12 }, backgroundColor: alpha(textColor, 0.12), borderRadius: { xs: 0.75, sm: 1, md: 1.25 } }} />
          </Box>
          
          {/* Main content area */}
          <Box
            sx={{
              backgroundColor: contentBg,
              borderRadius: '0 0 12px 12px',
              p: { xs: 1, sm: 1.25, md: 1.5 },
              minHeight: { xs: 100, sm: 110, md: 120 },
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {/* Content wrapper for centering */}
            <Box sx={{ 
              display: 'flex', 
              gap: { xs: 0.75, sm: 1, md: 1.25 },
              width: '100%',
              maxWidth: '260px',
              margin: '0 auto',
            }}>
              <Box sx={{ width: { xs: 44, sm: 50, md: 56 } }}>
                {[...Array(6)].map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      height: { xs: 6, sm: 7, md: 8 },
                      backgroundColor: alpha(textColor, 0.18),
                      borderRadius: { xs: 0.5, sm: 0.75, md: 1 },
                      mb: { xs: 0.9, sm: 1.1, md: 1.3 },
                    }}
                  />
                ))}
              </Box>
              
              {/* Main content */}
              <Box sx={{ flex: 1 }}>
                {/* Search bar */}
                <Box sx={{ height: { xs: 14, sm: 16, md: 18 }, backgroundColor: alpha(textColor, 0.08), borderRadius: { xs: 1, sm: 1, md: 1.25 }, mb: { xs: 0.75, sm: 1, md: 1.25 }, display: 'flex', alignItems: 'center', px: { xs: 0.75, sm: 1, md: 1.25 } }}>
                  <Box sx={{ 
                    width: { xs: 8, sm: 9, md: 10 }, 
                    height: { xs: 8, sm: 9, md: 10 }, 
                    backgroundColor: alpha(textColor, 0.25), 
                    borderRadius: '50%'
                  }} />
                </Box>
                
                {/* Content bars with colors */}
                <Stack spacing={{ xs: 0.25, sm: 0.375, md: 0.5 }}>
                  <Box sx={{ height: { xs: 3.5, sm: 4, md: 4 }, backgroundColor: '#2196f3', borderRadius: 1, width: '70%' }} />
                  <Box sx={{ 
                    height: { xs: 3.5, sm: 4, md: 4 }, 
                    backgroundColor: '#4caf50', 
                    borderRadius: 1, 
                    width: '85%' 
                  }} />
                  <Box sx={{ 
                    height: { xs: 3.5, sm: 4, md: 4 }, 
                    backgroundColor: '#e91e63', 
                    borderRadius: 1, 
                    width: '60%' 
                  }} />
                </Stack>
                
                {/* Content blocks */}
                <Box sx={{ 
                  mt: { xs: 0.5, sm: 0.75, md: 1 }, 
                  display: 'flex', 
                  gap: { xs: 0.5, sm: 0.75, md: 1 } 
                }}>
                  <Box sx={{ width: { xs: 24, sm: 28, md: 30 }, height: { xs: 16, sm: 18, md: 20 }, backgroundColor: alpha(textColor, 0.12), borderRadius: { xs: 1, sm: 1, md: 1.25 } }} />
                  <Box sx={{ 
                    width: { xs: 32, sm: 36, md: 38 }, 
                    height: { xs: 16, sm: 18, md: 20 }, 
                    backgroundColor: alpha(textColor, 0.12), 
                    borderRadius: { xs: 1, sm: 1, md: 1.25 } 
                  }} />
                </Box>
                
                {/* Additional content */}
                <Box sx={{ mt: 1 }}>
                  <Box sx={{ height: 3, backgroundColor: alpha(textColor, 0.18), borderRadius: 1, width: '90%', mb: 0.5 }} />
                  <Box sx={{ height: 3, backgroundColor: alpha(textColor, 0.18), borderRadius: 1, width: '75%', mb: 0.5 }} />
                  <Box sx={{ height: 3, backgroundColor: alpha(textColor, 0.18), borderRadius: 1, width: '80%' }} />
                </Box>
              </Box>
              
              {/* Right sidebar with chart */}
              <Box sx={{ width: { xs: 32, sm: 36, md: 40 } }}>
                <Box
                  sx={{
                    width: '100%',
                    height: { xs: 28, sm: 32, md: 36 },
                    border: `1px solid ${alpha(textColor, 0.2)}`,
                    borderRadius: { xs: 0.5, sm: 0.75, md: 1 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: { xs: 0.5, sm: 0.75, md: 1 },
                  }}
                >
                  {/* Donut chart representation */}
                  <Box
                    sx={{
                      width: { xs: 16, sm: 18, md: 20 },
                      height: { xs: 16, sm: 18, md: 20 },
                      border: `${2}px solid ${alpha(textColor, 0.2)}`,
                      borderTop: `${2}px solid ${isDark ? '#ff7043' : '#f57c00'}`,
                      borderRadius: '50%',
                    }}
                  />
                </Box>
                
                {/* Status indicators */}
                <Stack spacing={{ xs: 0.25, sm: 0.375, md: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 0.375, md: 0.5 } }}>
                    <Box sx={{ 
                      width: { xs: 3, sm: 3.5, md: 4 }, 
                      height: { xs: 3, sm: 3.5, md: 4 }, 
                      backgroundColor: '#4caf50', 
                      borderRadius: '50%' 
                    }} />
                    <Box sx={{ 
                      height: { xs: 1.5, sm: 1.75, md: 2 }, 
                      backgroundColor: alpha(textColor, 0.2), 
                      borderRadius: { xs: 0.25, sm: 0.375, md: 0.5 }, 
                      flex: 1 
                    }} />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 0.375, md: 0.5 } }}>
                    <Box sx={{ 
                      width: { xs: 3, sm: 3.5, md: 4 }, 
                      height: { xs: 3, sm: 3.5, md: 4 }, 
                      backgroundColor: '#2196f3', 
                      borderRadius: '50%' 
                    }} />
                    <Box sx={{ 
                      height: { xs: 1.5, sm: 1.75, md: 2 }, 
                      backgroundColor: alpha(textColor, 0.2), 
                      borderRadius: { xs: 0.25, sm: 0.375, md: 0.5 }, 
                      flex: 1 
                    }} />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 0.375, md: 0.5 } }}>
                    <Box sx={{ 
                      width: { xs: 3, sm: 3.5, md: 4 }, 
                      height: { xs: 3, sm: 3.5, md: 4 }, 
                      backgroundColor: '#e91e63', 
                      borderRadius: '50%' 
                    }} />
                    <Box sx={{ 
                      height: { xs: 1.5, sm: 1.75, md: 2 }, 
                      backgroundColor: alpha(textColor, 0.2), 
                      borderRadius: { xs: 0.25, sm: 0.375, md: 0.5 }, 
                      flex: 1 
                    }} />
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Box>
        </Box>
      </CardContent>
      
      {/* Selection indicator */}
      {isSelected && (
        <Box
          sx={{
            position: 'absolute',
            bottom: { xs: 8, sm: 10, md: 12 },
            left: '50%',
            transform: 'translateX(-50%)',
            width: { xs: 12, sm: 14, md: 16 },
            height: { xs: 12, sm: 14, md: 16 },
            backgroundColor: muiTheme.palette.primary.main,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: { xs: 4, sm: 5, md: 6 },
              height: { xs: 4, sm: 5, md: 6 },
              backgroundColor: 'white',
              borderRadius: '50%',
            }}
          />
        </Box>
      )}
    </Card>
  );
};

export const ThemeVisualSelector: React.FC<ThemeVisualSelectorProps> = ({
  currentMode,
  onModeChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const { resolvedMode } = useThemeContext();
  const isAutomatic = currentMode === 'auto';
  const currentThemeForCards = isAutomatic ? resolvedMode : currentMode;
  
  const handleLightSelect = () => {
    if (!disabled) {
      onModeChange('light');
    }
  };
  
  const handleDarkSelect = () => {
    if (!disabled) {
      onModeChange('dark');
    }
  };
  
  const handleAutomaticToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled) {
      if (event.target.checked) {
        onModeChange('auto');
      } else {
        // If turning off automatic, keep whatever the system currently resolves to
        onModeChange(resolvedMode);
      }
    }
  };

  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: '100%',
    }}>
      <Stack spacing={{ xs: 2, sm: 3 }}>
        {/* Theme preview cards */}
        <Box sx={{ 
          width: '100%',
          overflow: 'hidden',          
        }}>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={{ xs: 2.5, sm: 3, md: 4 }}
            sx={{ 
              width: '100%',
              alignItems: 'stretch',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              maxWidth: { sm: 520, md: 560 },
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: { xs: 1, sm: 1.25, md: 1.5 }, 
                  textAlign: 'center', 
                  fontWeight: 500,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                }}
              >
                Light
              </Typography>
              <ThemePreviewCard
                mode="light"
                isSelected={!isAutomatic && currentThemeForCards === 'light'}
                onSelect={handleLightSelect}
                disabled={disabled}
              />
            </Box>
            
            <Box sx={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              maxWidth: { sm: 520, md: 560 },
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: { xs: 1, sm: 1.25, md: 1.5 }, 
                  textAlign: 'center', 
                  fontWeight: 500,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                }}
              >
                Dark
              </Typography>
              <ThemePreviewCard
                mode="dark"
                isSelected={!isAutomatic && currentThemeForCards === 'dark'}
                onSelect={handleDarkSelect}
                disabled={disabled}
              />
            </Box>
          </Stack>
        </Box>
        
        {/* Automatic toggle row (label left, switch right) */}
        <Box
          sx={{
            width: '100%',            
            mx: 'auto',
            mt: { xs: 1.5, sm: 2 },
            pt: { xs: 1.5, sm: 2 },
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t('automatic')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('use_device_settings')}
              </Typography>
            </Box>
            <Switch
              checked={isAutomatic}
              onChange={handleAutomaticToggle}
              disabled={disabled}
            />
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};

export default ThemeVisualSelector;
